import {
  CommissionStatus,
  type CommissionResponseExtraFragment,
} from '@/affiliate-dashboard/graphql';
import { Avatar, AvatarFallback } from '@/components/base/avatar';
import { Label } from '@/components/base/label';
import { getConsistentColor } from '@/utils/randomizer';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import ReactCountryFlag from 'react-country-flag';
import { type IntlShape } from 'react-intl';

const columnHelper =
  createColumnHelper<CommissionResponseExtraFragment | null>();

export const defaultColumns = (
  intl: IntlShape,
  { t }: { t: (key: string) => string },
) => [
  columnHelper.accessor('createdAt', {
    id: 'createdAt	',
    header: () => t('table.Date'),
    cell: (info) => (
      <div className="flex flex-col">
        <span>{format(info.getValue() ?? '', 'dd MMM yyyy HH:mm')} </span>
        <span>{info.row.original?.orderNumber} </span>
      </div>
    ),
  }),
  columnHelper.accessor('order.amount', {
    id: 'orderAmount	',
    header: () => t('table.Sale'),
    cell: (props) => {
      return (
        <div className="flex flex-col">
          <span>
            {intl.formatNumber(props.row.original?.order?.amount ?? 0, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor('amount', {
    id: 'amount	',
    header: () => t('table.RecievedCommision'),
    cell: (props) => {
      const { level, amount } = props.row.original ?? {};

      return (
        <div className="flex items-center gap-1">
          <span className="font-bold text-default-gray-950 dark:text-white">
            {amount}
          </span>
          {(level ?? 0) > 1 && (
            <Label
              className="h-fit rounded border border-default-green-700 px-1 py-0"
              variant="green"
            >
              <span className="text-sm">{level}</span>
            </Label>
          )}
        </div>
      );
    },
  }),

  columnHelper.accessor('order.member.email', {
    id: 'order.member.email	',
    header: () => t('table.Customer'),
    cell: (info) => {
      const color = getConsistentColor(info.row.original?.id ?? '');
      const country = info.row.original?.member?.countryCode;
      const email = info.getValue();
      const initials = info.row.original?.order?.member?.initials;

      return (
        <div className="flex w-full items-center justify-start gap-3">
          {initials ? (
            <Avatar className="relative h-8 w-8 overflow-visible border-none">
              <AvatarFallback
                className="bg-opacity-25"
                style={{
                  backgroundColor: color,
                  color: 'white',
                }}
              >
                {initials}
              </AvatarFallback>
              {country && (
                <div className="absolute -bottom-1 right-0 z-0">
                  <ReactCountryFlag
                    countryCode={country}
                    style={{
                      height: '1.1rem',
                      width: '1.1rem',
                      borderRadius: '20%',
                      objectFit: 'cover',
                    }}
                    svg
                  />
                </div>
              )}
            </Avatar>
          ) : (
            '-'
          )}
          <span className="max-w-44 truncate">{email}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('order.level1Affiliate.level', {
    id: 'order.commissions.order	',
    header: () => t('table.RefferdBy'),
    cell: (info) => {
      const color = getConsistentColor(info.row.original?.id ?? '');
      const country = info.row.original?.member?.countryName;
      const initials = info.row.original?.order?.level1Affiliate?.initials;

      return (
        <div className="flex w-full items-center justify-start gap-3">
          {initials ? (
            <Avatar className="relative h-8 w-8 overflow-visible border-none">
              <AvatarFallback
                className="bg-opacity-25"
                style={{
                  backgroundColor: color,
                  color: 'white',
                }}
              >
                {initials}
              </AvatarFallback>
              {country && (
                <div className="absolute -bottom-1 right-0 z-40">
                  <ReactCountryFlag
                    countryCode={country}
                    style={{
                      height: '1.1rem',
                      width: '1.1rem',
                      borderRadius: '20%',
                      objectFit: 'cover',
                    }}
                    svg
                  />
                </div>
              )}
            </Avatar>
          ) : (
            '-'
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('status', {
    id: 'status	',
    header: () => t('table.PayedOut'),
    cell: (info) => {
      switch (info.getValue()) {
        case CommissionStatus.Paid:
          return (
            <div className="flex w-full items-center justify-end gap-3">
              <Label variant="green">{t('commissions.table.paid')}</Label>
            </div>
          );
        case CommissionStatus.Locked:
          return (
            <div className="flex w-full items-center justify-end gap-3">
              <Label variant="gray">{t('commissions.table.locked')}</Label>
            </div>
          );
        case CommissionStatus.Unlocked:
          return (
            <div className="flex w-full items-center justify-end gap-3">
              <Label variant="default">{t('commissions.table.unlocked')}</Label>
            </div>
          );
        case CommissionStatus.PayoutRequested:
          return (
            <div className="flex w-full items-center justify-end gap-3">
              <Label variant="default">
                {t('commissions.table.payoutRequested')}
              </Label>
            </div>
          );
        default:
          return (
            <div className="flex w-full items-center justify-end gap-3">
              <Label variant="default">{t('commissions.table.unlocked')}</Label>
            </div>
          );
      }
    },
  }),
];
